import React from 'react';
import type { InsurancePlan } from 'src/services/interfaces/insurance';

import { Checkbox } from '@components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/ui/accordion';

import { cn } from '@utils/index';

import type { SelectionOffering } from '../api/selection';
import { categoryCopy, MEDICAL_WAIVE_CERTIFICATION } from '../constants/copy';
import {
  YOU_PERSON_ID,
  deriveCoverage,
  depPersonId,
  isMedicalCategory,
  useSelectionStore,
} from '../store/selection';
import { W2_SELECT_TID } from '../constants/testids';
import { money } from '../format';
import {
  PlanDetailsAccordion,
  PlanLogo,
  PlanPremium,
  PlanStats,
} from './PlanCardParts';

const TID = W2_SELECT_TID.plans;

interface PlanCardProps {
  offering: SelectionOffering;
  /** Joined full plan record (WP6) — drives logo/description/stats/details. */
  detail?: InsurancePlan;
}

/**
 * One employer-offered plan line, in the B2C list-card design (the classes come
 * from `ListPlanCard`; see `PlanCardParts`) merged with the client-final W2
 * decision model:
 *   • an identity row — carrier logo, plan name, clamped description, the
 *     "$X / Monthly premium" column and the per-category stats column;
 *   • the "I waive … coverage" option as a RADIO at the TOP of the decision
 *     block (client-final copy, `../constants/copy.ts`) — it is the first
 *     control the employee can act on, ahead of who's-covered;
 *   • who's-covered as PER-PERSON checkboxes — [You] + each dependent by name
 *     (seeded from `context.dependents`); checking any person elects the line;
 *   • the certification accordion ONLY on the medical/Health waive (other lines
 *     waive without certification text);
 *   • the B2C "Show Plan Details" expander over `FormPlanDetails`.
 *
 * Nothing here is "absence as answer": until the person picks Waive or checks a
 * covered person, the line is undecided and the category cannot advance.
 *
 * Every `w2-select-plan-*` testid is preserved verbatim — they are a pinned e2e
 * contract (`e2e/w2-selection/`), so the restyle is presentation-only.
 */
export const PlanCard: React.FC<PlanCardProps> = ({ offering, detail }) => {
  const line = useSelectionStore((s) => s.lines[offering.planId]);
  const dependents = useSelectionStore((s) => s.context?.dependents);
  const togglePerson = useSelectionStore((s) => s.togglePerson);
  const waive = useSelectionStore((s) => s.waive);
  const setCertified = useSelectionStore((s) => s.setCertified);
  const [descriptionExpanded, setDescriptionExpanded] = React.useState(false);

  const copy = categoryCopy(offering.category);
  const medical = isMedicalCategory(offering.category);
  const decision = line?.decision ?? null;
  const covered = line?.covered ?? [];
  const isWaived = decision === 'Waived';
  const isElected = decision === 'Elected';

  const deps = dependents ?? [];
  const derivedCoverage = isElected ? deriveCoverage(covered, deps) : null;
  const electedPrice =
    derivedCoverage !== null
      ? offering.coverageOptions.find((o) => o.coverage === derivedCoverage)
          ?.price
      : undefined;
  // Before any election the column shows the INDIVIDUAL-tier price (captain
  // decision D3, matching the B2C card, which is priced pre-selection) so the
  // premium column is never empty; it re-prices live once persons are checked.
  const baselinePrice =
    offering.coverageOptions.find((o) => o.coverage === 1)?.price ??
    offering.coverageOptions[0]?.price;
  // Once ELECTED the column shows the elected tier's price or nothing — never
  // the Individual baseline, which would print a prominent dollar figure that
  // is NOT the price of the current selection, contradicting the "Price
  // unavailable for this selection" caption right below it.
  const shownPrice = isElected ? electedPrice : baselinePrice;

  const persons: Array<{ id: string; label: string; testidSuffix: string }> = [
    { id: YOU_PERSON_ID, label: 'You', testidSuffix: 'you' },
    ...deps.map((dep, i) => ({
      id: depPersonId(i),
      label: dep.firstName?.trim() || `Dependent ${i + 1}`,
      testidSuffix: `dep-${i}`,
    })),
  ];

  const title = detail?.name?.trim() || offering.shortName;
  const description = detail?.shortDescription?.trim();

  return (
    <div
      data-testid={`${TID.card}-${offering.planId}`}
      className={cn(
        'flex flex-col overflow-hidden rounded-2xl border bg-white p-3 shadow-[0px_8px_30px_rgba(15,15,15,0.05)] transition-all max-sm:p-2.5',
        isElected
          ? 'border-primary plan-selected-shadow'
          : isWaived
            ? 'border-[#ece5d8] bg-[#faf9f6]'
            : 'border-[#ece5d8]'
      )}
    >
      {/* Identity row — logo + name/description, premium column, stats column. */}
      <div className="flex flex-col gap-4 py-1 lg:flex-row lg:items-start lg:gap-3 md:py-2">
        <div className="flex flex-1 flex-row items-start gap-3 pr-2">
          <PlanLogo plan={detail} category={offering.category} />
          <div>
            <h3 className="mb-1.5 text-[16px] leading-[22px] font-bold text-neutral-900">
              {title}
            </h3>
            {description ? (
              <p
                onClick={() => setDescriptionExpanded((v) => !v)}
                title="Click to expand"
                className={cn(
                  'max-w-xl cursor-pointer text-xs leading-4 font-normal text-[#4a473f] transition-all duration-200',
                  !descriptionExpanded && 'line-clamp-3'
                )}
              >
                {description}
              </p>
            ) : (
              <p className="text-xs leading-4 text-[#8a8aa0]">
                {offering.category}
              </p>
            )}
          </div>
        </div>

        <PlanPremium
          amount={shownPrice !== undefined ? money(shownPrice) : '—'}
          testId={`${TID.price}-${offering.planId}`}
          className="shrink-0 lg:w-[22%] lg:border-r lg:border-l lg:border-[#efe8dc] lg:px-4"
        />

        <PlanStats
          plan={detail}
          className="lg:w-[34%] lg:shrink-0 lg:px-3 max-lg:border-t max-lg:border-[#efe8dc] max-lg:pt-3"
        />
      </div>

      {/* Decision block — waive FIRST (client copy), then who's-covered. */}
      <div className="mt-4 border-t border-[#ede6da] pt-4">
        <label
          className={cn(
            'flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 text-[14px] transition',
            isWaived
              ? 'border-[#5a5a70] bg-white font-semibold text-[#1a1a2e]'
              : 'border-[#ece5d8] bg-white text-[#5a5a70] hover:border-[#c9c4b6]'
          )}
        >
          <input
            type="radio"
            name={`decision-${offering.planId}`}
            data-testid={`${TID.waive}-${offering.planId}`}
            checked={isWaived}
            onChange={() => waive(offering.planId)}
            className="size-4 accent-primary"
          />
          {copy.waiveLabel}
        </label>

        {isWaived && medical && (
          <Accordion
            type="single"
            collapsible
            defaultValue="certify"
            className="mt-3"
            data-testid={`${TID.certifyAccordion}-${offering.planId}`}
          >
            <AccordionItem value="certify" className="border-b-0">
              <AccordionTrigger className="text-[#5a5a70]">
                Health waiver certification
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id={`certify-${offering.planId}`}
                    data-testid={`${TID.certifyCheckbox}-${offering.planId}`}
                    checked={line?.certified ?? false}
                    onCheckedChange={(checked) =>
                      setCertified(offering.planId, checked === true)
                    }
                    className="mt-1 size-5 shrink-0"
                  />
                  <label
                    htmlFor={`certify-${offering.planId}`}
                    className="cursor-pointer text-[13px] leading-5 text-[#3a3a4e]"
                  >
                    {/* One source of truth: the SAME constant stamped into
                        `certificationText` and submitted (fe-fb #6). */}
                    {MEDICAL_WAIVE_CERTIFICATION}
                  </label>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        <div className="mt-4">
          <p className="mb-2 text-[13px] font-semibold text-[#5a5a70]">
            {copy.prompt}
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {persons.map((person) => {
              const checked = isElected && covered.includes(person.id);
              return (
                <label
                  key={person.id}
                  className="flex cursor-pointer items-center gap-3 text-[14px] text-[#1a1a2e]"
                >
                  <Checkbox
                    data-testid={`${TID.person}-${offering.planId}-${person.testidSuffix}`}
                    checked={checked}
                    onCheckedChange={(value) =>
                      togglePerson(offering.planId, person.id, value === true)
                    }
                    className="size-5"
                  />
                  {person.label}
                </label>
              );
            })}
          </div>
          {isElected && electedPrice === undefined && (
            <p className="mt-2 text-[13px] text-[#8a5a00]">
              {covered.length > 0
                ? 'Price unavailable for this selection'
                : 'Choose who’s covered'}
            </p>
          )}
        </div>
      </div>

      <PlanDetailsAccordion plan={detail} planId={offering.planId} />
    </div>
  );
};
